import * as React from 'react';
import Modal from 'react-modal';
import { InjectedRouter, PlainRoute } from 'react-router';
import { Location } from 'lib/ReactRouterTypes';

import AdminOrgApprovalSettingsConstants from '../constants/AdminOrgApprovalSettingsConstants';
import Button from 'components/Button';
import ConfirmDialog from 'components/ConfirmDialog';
import Container from 'components/Container';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Radio from 'components/Radio';
import {
  renderRadioSelectLabel,
  renderRadioSelectField,
} from 'admin/components/AdminDefaultClassSettings/components/AdminDefaultClassSettingsView';
import { SetOrgApprovalSettingsInput } from 'globalTypes';
import Text from 'components/Text';
import TitleHeader from 'components/TitleHeader';
import Tooltip from 'components/Tooltip';

enum OrgTeacherSignupState {
  open = 'open',
  requireApproval = 'requireApproval',
}

enum OrgClassCreationState {
  open = 'open',
  requireApproval = 'requireApproval',
}

export type Props = {
  orgUuid: string;
  route: PlainRoute;
  router: InjectedRouter;
  approvalSettings: SetOrgApprovalSettingsInput;
  onSave: (arg0: { variables: { input: SetOrgApprovalSettingsInput } }) => void;
};

type State = {
  approvePendingRequestsDialogIsOpen: boolean;
  backfillDialogIsOpen: boolean;
  unsavedChangesDialogIsOpen: boolean;
  studentAndParentClassCreationInitialSelect?: OrgClassCreationState;
  teacherSignupInitialSelect?: OrgTeacherSignupState;
  studentAndParentClassCreationSelect?: OrgClassCreationState;
  teacherSignupSelect?: OrgTeacherSignupState;
  nextLocation?: Location;
};

export default class AdminOrgApprovalSettingsView extends React.Component<Props, State> {
  state: State = {
    approvePendingRequestsDialogIsOpen: false,
    backfillDialogIsOpen: false,
    unsavedChangesDialogIsOpen: false,
  };

  componentDidMount() {
    // https://github.com/ReactTraining/react-router/blob/v3/docs/API.md#setrouteleavehookroute-hook
    // this registers `handleAttemptedRouteChange` to fire anytime the user tries to leave the current route
    this.props.router?.setRouteLeaveHook(this.props.route, this.handleAttemptedRouteChange);

    const { approvalSettings } = this.props;
    const { gateTeacherRole, gateClasses } = approvalSettings;
    const teacherSignupValue = gateTeacherRole
      ? OrgTeacherSignupState.requireApproval
      : OrgTeacherSignupState.open;
    const classCreationValue = gateClasses
      ? OrgClassCreationState.requireApproval
      : OrgClassCreationState.open;
    this.setState({
      teacherSignupInitialSelect: teacherSignupValue,
      teacherSignupSelect: teacherSignupValue,
      studentAndParentClassCreationInitialSelect: classCreationValue,
      studentAndParentClassCreationSelect: classCreationValue,
    });
  }

  showUnsavedChangesDialog = () => {
    this.setState({ unsavedChangesDialogIsOpen: true });
  };

  hideUnsavedChangesDialog = () => {
    this.setState({ unsavedChangesDialogIsOpen: false });
  };

  handleAttemptedRouteChange = (nextLocation?: Location) => {
    // if the user has changed their settings and isn't already viewing the unsaved changes dialog,
    // we should mount the dialog and store the nextLocation that the user attempted to visit
    if (this.userHasChangedSettings() && nextLocation && !this.state.unsavedChangesDialogIsOpen) {
      this.setState({ nextLocation }, this.showUnsavedChangesDialog);
      return false;
    }
  };

  forwardUserToNextLocation = () => {
    // we should only be calling this method from the confirmation dialog, so we *should* always
    // have a nextLocation, but we don't initialize this component with a nextLocation so we
    // need to perform this check
    const { nextLocation } = this.state;
    return nextLocation
      ? this.props.router.push(nextLocation)
      : console.error(
          'AdminDefaultClassSettingsView - tried to route user to nonexistent nextLocation'
        );
  };

  userHasChangedSettings = () => {
    const {
      teacherSignupSelect,
      teacherSignupInitialSelect,
      studentAndParentClassCreationSelect,
      studentAndParentClassCreationInitialSelect,
    } = this.state;

    return (
      teacherSignupSelect !== teacherSignupInitialSelect ||
      studentAndParentClassCreationSelect !== studentAndParentClassCreationInitialSelect
    );
  };

  renderBackfillModalParagraph = (
    pendingTeacherRequestCopy: string,
    pendingClassRequestCopy: string
  ) => (
    <React.Fragment>
      {pendingTeacherRequestCopy && <Text element="p">{pendingTeacherRequestCopy}</Text>}
      {pendingClassRequestCopy && <Text>{pendingClassRequestCopy}</Text>}
    </React.Fragment>
  );

  // Approve pending requests dialog actions

  approveRequests = () => {
    // Placeholder for all the actions needed when the submit button is clicked i.e. mutation + close dialog
    this.hideApprovePendingRequestsDialog();
  };

  hideApprovePendingRequestsDialog = () => {
    this.setState({ approvePendingRequestsDialogIsOpen: false });
  };

  // Backfill dialog actions

  applyBackfill = () => {
    // Placeholder for all the actions needed when the submit button is clicked i.e. mutation + close dialog
    this.hideBackfillDialog();
  };

  hideBackfillDialog = () => {
    this.setState({ backfillDialogIsOpen: false });
  };

  // Handle form changes and submit

  handleTeacherSignupChange = (option: OrgTeacherSignupState) => {
    this.setState({ teacherSignupSelect: option });
  };

  handleStudentAndParentClassCreationChange = (option: OrgClassCreationState) => {
    this.setState({ studentAndParentClassCreationSelect: option });
  };

  handleSubmit = () => {
    const { onSave, orgUuid } = this.props;
    const { teacherSignupSelect, studentAndParentClassCreationSelect } = this.state;

    onSave({
      variables: {
        input: {
          orgUuid,
          gateTeacherRole: teacherSignupSelect === OrgTeacherSignupState.requireApproval,
          gateClasses:
            studentAndParentClassCreationSelect === OrgClassCreationState.requireApproval,
        },
      },
    });

    this.setState({
      teacherSignupInitialSelect: teacherSignupSelect,
      studentAndParentClassCreationInitialSelect: studentAndParentClassCreationSelect,
    });
  };

  // render radio buttons

  renderTeacherSignupRadio = () => {
    const { teacherSignupSelect } = this.state;
    const radioButtons = (Object.keys(OrgTeacherSignupState) as OrgTeacherSignupState[]).map(
      orgTeacherSignupOption => (
        <Radio
          key={orgTeacherSignupOption}
          label={renderRadioSelectLabel(
            AdminOrgApprovalSettingsConstants.TEACHER_SIGNUP_TO_LABEL[orgTeacherSignupOption],
            AdminOrgApprovalSettingsConstants.TEACHER_SIGNUP_TO_HELPTEXT[orgTeacherSignupOption],
            false
          )}
          checked={orgTeacherSignupOption === teacherSignupSelect}
          onChange={() => this.handleTeacherSignupChange(orgTeacherSignupOption)}
          disabled={false}
          data-test={`teacher-signup-${orgTeacherSignupOption}`}
        />
      )
    );
    return renderRadioSelectField(
      AdminOrgApprovalSettingsConstants.HEADING_FOR_SETTING_TYPE.teacherSignup,
      radioButtons as React.ReactElement[]
    );
  };

  renderStudentAndParentClassCreationRadio = () => {
    const { teacherSignupSelect, studentAndParentClassCreationSelect } = this.state;
    const radioButtons = (Object.keys(OrgClassCreationState) as OrgClassCreationState[]).map(
      orgClassCreationOption => {
        const enabled = !(
          teacherSignupSelect === OrgTeacherSignupState.open &&
          orgClassCreationOption === OrgClassCreationState.requireApproval
        );

        const RadioButtonElement = (
          <Radio
            key={orgClassCreationOption}
            label={renderRadioSelectLabel(
              AdminOrgApprovalSettingsConstants.CLASS_CREATION_TO_LABEL[orgClassCreationOption],
              AdminOrgApprovalSettingsConstants.CLASS_CREATION_TO_HELPTEXT[orgClassCreationOption],
              false
            )}
            checked={studentAndParentClassCreationSelect === orgClassCreationOption}
            onChange={() => this.handleStudentAndParentClassCreationChange(orgClassCreationOption)}
            disabled={!enabled}
            data-test={`class-creation-${orgClassCreationOption}`}
          />
        );

        if (!enabled) {
          return (
            <LayerTrigger
              layer={<Tooltip>{AdminOrgApprovalSettingsConstants.CLASS_CREATION_TOOLTIP}</Tooltip>}
              delay={50}
              key={`class-creation-${orgClassCreationOption}-layer-trigger`}
              placement="bottom">
              <div>{RadioButtonElement}</div>
            </LayerTrigger>
          );
        }
        return RadioButtonElement;
      }
    );
    return renderRadioSelectField(
      AdminOrgApprovalSettingsConstants.HEADING_FOR_SETTING_TYPE.classCreation,
      radioButtons as React.ReactElement[]
    );
  };

  render() {
    const {
      approvePendingRequestsDialogIsOpen,
      backfillDialogIsOpen,
      unsavedChangesDialogIsOpen,
      studentAndParentClassCreationSelect,
      teacherSignupSelect,
    } = this.state;
    const {
      PENDING_REQUESTS_MODAL,
      BACKFILL_MODAL,
      HEADER_TITLE,
      HEADER_SUBTEXT,
      CONFIRM_DIALOG,
    } = AdminOrgApprovalSettingsConstants;

    const submitIsEnabled =
      this.userHasChangedSettings() &&
      !(
        teacherSignupSelect === OrgTeacherSignupState.open &&
        studentAndParentClassCreationSelect === OrgClassCreationState.requireApproval
      );

    return (
      <div>
        <Modal isOpen={unsavedChangesDialogIsOpen} data-test="unsaved-changes-dialog">
          <ConfirmDialog
            glyph="caution"
            glyphColor="lava"
            title={CONFIRM_DIALOG.title}
            subtext={CONFIRM_DIALOG.body}
            onConfirm={this.hideUnsavedChangesDialog}
            onCancel={this.forwardUserToNextLocation}
            actionButtonText={CONFIRM_DIALOG.buttons.action}
            cancelButtonText={CONFIRM_DIALOG.buttons.cancel}
            onRequestHide={this.hideUnsavedChangesDialog}
          />
        </Modal>
        <Modal
          isOpen={approvePendingRequestsDialogIsOpen}
          data-test="approve-pending-requests-dialog">
          <ConfirmDialog
            title={PENDING_REQUESTS_MODAL.title}
            subtext={<Text element="p">{PENDING_REQUESTS_MODAL.subtext(111, 111)}</Text>}
            actionButtonText={PENDING_REQUESTS_MODAL.buttons.submit.copy}
            onConfirm={this.approveRequests}
            cancelButtonText={PENDING_REQUESTS_MODAL.buttons.cancel.copy}
            onRequestHide={this.hideApprovePendingRequestsDialog}
          />
        </Modal>
        <Modal isOpen={backfillDialogIsOpen} data-test="backfill-dialog">
          <ConfirmDialog
            title={BACKFILL_MODAL.title(111, 222)}
            subtext={this.renderBackfillModalParagraph(
              BACKFILL_MODAL.subtext.teachers(111),
              BACKFILL_MODAL.subtext.classes(222)
            )}
            actionButtonText={BACKFILL_MODAL.buttons.submit.copy}
            onConfirm={this.applyBackfill}
            onRequestHide={this.hideBackfillDialog}
            cancelButtonText={BACKFILL_MODAL.buttons.learnMore.copy}
            onCancel={() => {
              window.open(BACKFILL_MODAL.buttons.learnMore.href);
            }}
          />
        </Modal>
        <Container size="medium" align="left" flush>
          <TitleHeader
            title={HEADER_TITLE}
            subtext={
              <Text>
                {HEADER_SUBTEXT.text}{' '}
                <Link href={HEADER_SUBTEXT.learnMoreLink.href} target="_blank">
                  {HEADER_SUBTEXT.learnMoreLink.copy}
                </Link>
              </Text>
            }
            textAlign="left"
          />
          {this.renderTeacherSignupRadio()}
          {this.renderStudentAndParentClassCreationRadio()}
          <Button disabled={!submitIsEnabled} onClick={this.handleSubmit} data-test="submit-button">
            Save
          </Button>
        </Container>
      </div>
    );
  }
}
